import { useState } from "react";
import {
  ConfirmDeleteModal,
  EntitySlideout,
  saveErrorReason,
  saveErrorToast,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useCalculationsList } from "./api/useCalculationsList";
import { useCalculationOperands } from "./api/useCalculationOperands";
import { useCalculationMutations } from "./api/useCalculationMutations";
import { CalculationsList } from "./CalculationsList";
import { CalculationForm } from "./CalculationForm";
import {
  emptyCalculationForm,
  type Calculation,
  type CalculationFormValues,
  type OperandGroups,
} from "./types";

const isValidCalculation = (v: CalculationFormValues): boolean =>
  v.name.trim().length >= 3 && v.expression.trim() !== "";

export interface CalculationsTabProps {
  questionnaireId: string;
}

const toFormValues = (c: Calculation): CalculationFormValues => ({
  name: c.name,
  expression: c.expression,
});

export const CalculationsTab = ({
  questionnaireId,
}: CalculationsTabProps): JSX.Element => {
  const calculations = useCalculationsList(questionnaireId);
  const { questions, lookups } = useCalculationOperands(questionnaireId);
  const mutations = useCalculationMutations(questionnaireId);
  const sel = useBatchSelection();

  const [view, setView] = useState<
    { mode: "list" } | { mode: "form"; editing?: Calculation }
  >({ mode: "list" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const editingId = view.mode === "form" ? view.editing?.id : undefined;
  const operands: OperandGroups = {
    questions,
    lookups,
    calculations: calculations.rows
      .filter((c) => c.id !== editingId)
      .map((c) => ({ slug: c.slug, name: c.name })),
  };

  const saveCalculation = async (vals: CalculationFormValues) => {
    try {
      if (view.mode === "form" && view.editing) {
        await mutations.update(view.editing.id, vals);
      } else {
        await mutations.create(vals);
      }
      setView({ mode: "list" });
      calculations.refetch();
    } catch (err) {
      setToast(saveErrorToast(err, "calculation"));
    }
  };

  const confirmBatchDelete = async () => {
    setConfirmDelete(false);
    const ids = [...sel.selectedIds];
    const failures: BatchFailure[] = [];
    for (const id of ids) {
      try {
        await mutations.remove(id);
      } catch (err) {
        failures.push({ reason: saveErrorReason(err) });
      }
    }
    if (failures.length > 0) {
      setToast({
        variant: "danger",
        text: withFailureReasons(
          `Failed to delete ${failures.length} of ${ids.length} calculation(s).`,
          failures
        ),
      });
    }
    sel.clear();
    calculations.refetch();
  };

  if (calculations.isLoading) return <ScopeStackSpinner />;

  return (
    <div className="calculations-tab">
      <CalculationsList
        rows={calculations.rows}
        selectedIds={sel.selectedIds}
        setSelectedIds={sel.setSelectedIds}
        onAdd={() => setView({ mode: "form" })}
        onEdit={(c) => setView({ mode: "form", editing: c })}
        onBatchDelete={() => setConfirmDelete(true)}
      />

      <EntitySlideout
        isOpen={view.mode === "form"}
        initialValues={
          view.mode === "form" && view.editing
            ? toFormValues(view.editing)
            : emptyCalculationForm
        }
        title={
          view.mode === "form" && view.editing
            ? "Edit Calculation"
            : "Add Calculation"
        }
        isValid={isValidCalculation}
        onClose={() => setView({ mode: "list" })}
        onSave={saveCalculation}
        className="wideSlideout"
      >
        {(values, onChange) => (
          <CalculationForm
            mode={view.mode === "form" && view.editing ? "edit" : "create"}
            values={values}
            onChange={onChange}
            slug={(view.mode === "form" && view.editing?.slug) || ""}
            operands={operands}
          />
        )}
      </EntitySlideout>

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete calculations?"
        message={`Delete ${sel.selectedIds.length} selected calculation(s)?`}
        confirmLabel="Delete"
        onConfirm={confirmBatchDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CalculationsTab;
